import {
  all,
  call,
  put,
  take,
  fork,
  race,
  select,
  takeLatest,
  takeEvery,
  cancelled
} from "redux-saga/effects";
import { eventChannel, END } from "redux-saga";
import { CancelToken } from "axios";
import get from "lodash/get";
import mapValues from "lodash/mapValues";
import { saveAs } from "file-saver";

import {
  getProspectDocuments,
  uploadProspectDocument,
  downloadProspectDocument
} from "../../api/apiClient";
import {
  getProspectId,
  getAuthorizationHeader,
  getAppConfig,
  getDocuments,
  getOrganizationInfo,
  getOrgKYCDetails
} from "../selectors/appConfig";
import {
  RETRIEVE_DOC_UPLOADER,
  DOC_UPLOADER,
  CANCEL_DOC_UPLOAD,
  DOWNLOAD_DOCUMENT_FILE,
  uploadFilesProgress,
  uploadFilesFail,
  getProspectDocumentsSuccess,
  getProspectDocumentsFail,
  ADD_OTHER_DOCUMENT,
  DELETE_OTHER_DOCUMENT,
  SAVE_AND_RETRIEVE_DOC_UPLOADER,
  ADD_MULTI_DOCUMENT
} from "../actions/uploadDocuments";
import {
  SEND_PROSPECT_TO_API_FAIL,
  SEND_PROSPECT_TO_API_SUCCESS,
  sendProspectToAPI
} from "../../store/actions/sendProspectToAPI";
import { updateProspect, setConfig } from "../actions/appConfig";
import { log } from "../../utils/loggger";
import {
  concatCompanyDocs,
  concatStakeholdersDocs,
  createDocumentMapper,
  appendDocumentKey,
  appendMultiDocumentKey,
  extractDocumentTypes
} from "../../utils/documents";
import { cloneDeep } from "../../utils/cloneDeep";
import {
  COMPANY_DOCUMENTS,
  OTHER_DOCUMENTS,
  STAKEHOLDER_DOCUMENTS,
  COMPANY_BANK_STATEMENTS,
  COMPANY_ADDRESS_PROOF,
  COMPANY_INVOICES,
  PERSONAL_BANK_STATEMENTS,
  PERSONAL_BACKGROUND,
  COMPANY_BANK_STATEMENTS_DOCTYPE,
  COMPANY_ADDRESS_PROOF_DOCTYPE,
  COMPANY_INVOICES_DOCTYPE,
  PERSONAL_BANK_STATEMENTS_DOCTYPE,
  PERSONAL_BACKGROUND_DOCTYPE,
  companyMultiDocs,
  stakeholderMultiDocs,
  KYC_ANNEXURE_DOCUMENTS
} from "./../../constants";
import { AUTO } from "../../constants";

export function createUploader(prospectId, data, source, headers) {
  let emit;
  const chan = eventChannel(emitter => {
    emit = emitter;
    return () => {};
  });
  const onUploadProgress = ({ total, loaded }) => {
    const percentage = Math.round((loaded * 100) / total);
    emit(percentage);
    if (percentage === 100) emit(END);
  };

  const uploadPromise = uploadProspectDocument.send({
    prospectId,
    data,
    source,
    onUploadProgress,
    headers
  });
  return [uploadPromise, chan];
}

export function* uploadProgressWatcher(chan, documentKey) {
  while (true) {
    const progress = yield take(chan);
    yield put(uploadFilesProgress({ [documentKey]: progress }));
  }
}

export function* saveProspectAndGetProspectDocumentsSaga() {
  yield put(sendProspectToAPI(AUTO));
  yield race([take(SEND_PROSPECT_TO_API_SUCCESS), take(SEND_PROSPECT_TO_API_FAIL)]);
  yield call(getProspectDocumentsSaga);
}

/* istanbul ignore next */
function* getProspectDocumentsSaga() {
  const headers = yield select(getAuthorizationHeader);
  const prospectID = yield select(getProspectId);
  const existDocuments = yield select(getDocuments);
  const organizationInfo = yield select(getOrganizationInfo);
  const orgKYCDetails = yield select(getOrgKYCDetails);
  const isDocsUploaded =
    existDocuments &&
    existDocuments.companyDocuments.length > 0 &&
    existDocuments.stakeholdersDocuments;
  try {
    const { data } = yield call(getProspectDocuments.retriveDocuments, prospectID, headers);
    const appConfig = cloneDeep(yield select(getAppConfig));
    appConfig.documentTypes = extractDocumentTypes(data);
    const companyDocs = appendDocumentKey(data.companyDocuments);
    // ro-assist-brd2-1
    const companyBankStatements = appendMultiDocumentKey(data.companyBankStatements);
    const companyAddressProof = appendMultiDocumentKey(data.companyAddressProof);
    const companyInvoices = appendMultiDocumentKey(data.companyInvoices);
    const stakeHoldersDocs = mapValues(
      data.stakeholdersDocuments,
      (stakeHolder, stakeholderName) => ({
        ...stakeHolder,
        documents: appendDocumentKey(stakeHolder.documents, stakeholderName),
        personalBankStatements: appendMultiDocumentKey(
          stakeHolder.personalBankStatements,
          stakeholderName
        ),
        personalBackground: appendMultiDocumentKey(stakeHolder.personalBackground, stakeholderName)
      })
    );

    const kycAnnexureDocs = appendDocumentKey(data.kycAnnexureDocuments);

    const companyDocuments = concatCompanyDocs(
      isDocsUploaded ? existDocuments.companyDocuments : [],
      companyDocs
    );
    const kycAnnexureDocuments = concatCompanyDocs(
      isDocsUploaded ? existDocuments.kycAnnexureDocuments : [],
      kycAnnexureDocs
    );
    companyBankStatements.documents = concatCompanyDocs(
      isDocsUploaded && existDocuments.companyBankStatements
        ? existDocuments.companyBankStatements.documents
        : [],
      companyBankStatements.documents,
      COMPANY_BANK_STATEMENTS,
      organizationInfo,
      orgKYCDetails
    );
    companyAddressProof.documents = concatCompanyDocs(
      isDocsUploaded && existDocuments.companyAddressProof
        ? existDocuments.companyAddressProof.documents
        : [],
      companyAddressProof.documents,
      COMPANY_ADDRESS_PROOF,
      organizationInfo,
      orgKYCDetails
    );
    companyInvoices.documents = concatCompanyDocs(
      isDocsUploaded && existDocuments.companyInvoices
        ? existDocuments.companyInvoices.documents
        : [],
      companyInvoices.documents,
      COMPANY_INVOICES,
      organizationInfo,
      orgKYCDetails
    );
    const stakeholdersDocuments = concatStakeholdersDocs(
      stakeHoldersDocs,
      isDocsUploaded ? existDocuments.stakeholdersDocuments : {},
      organizationInfo,
      orgKYCDetails
    );

    const otherDocuments = existDocuments.otherDocuments || [];
    const isCompanyDocUpdate = existDocuments.isCompanyDocUpdate;
    const isKycDocUpdate = existDocuments.isKycDocUpdate;

    appConfig.prospect.documents = {
      companyDocuments,
      companyBankStatements,
      companyAddressProof,
      companyInvoices,
      stakeholdersDocuments,
      kycAnnexureDocuments,
      isCompanyDocUpdate,
      isKycDocUpdate,
      otherDocuments
    };
    yield put(updateProspect(appConfig));
    yield put(getProspectDocumentsSuccess());
  } catch (error) {
    yield put(getProspectDocumentsFail());
    log(error);
  }
}

export function* initDocumentUpload() {
  try {
    const headers = yield select(getAuthorizationHeader);
    const resp = yield call(uploadProspectDocument.requestClientToken, headers);
    return { token: resp.data?.token, jwtToken: resp.data?.jwtToken };
  } catch (error) {
    log(error);
  }
}

/* istanbul ignore next */
function* uploadDocumentsBgSync({
  data,
  docProps,
  docOwner,
  documentKey,
  stakeholderIndex,
  userFileName,
  isDocUpdate
}) {
  const source = CancelToken.source();

  try {
    const state = yield select();
    const config = { ...state.appConfig };
    const prospectId = getProspectId(state);
    const { token = "", jwtToken = "" } = yield call(initDocumentUpload);
    const headers = {
      headers: token
        ? { Authorization: `${token}`, Accept: "application/json" }
        : { Accept: "application/json" }
    };

    data.append("jwt", jwtToken);

    const [uploadPromise, chan] = yield call(createUploader, prospectId, data, source, headers);

    yield fork(uploadProgressWatcher, chan, documentKey);

    const response = yield call(() => uploadPromise);

    const documents = cloneDeep(config.prospect.documents);
    const fileName = get(response, "data.fileName", "");
    const additionalProps = { ...docProps, fileName, fileDescription: userFileName };

    if (docOwner === COMPANY_DOCUMENTS) {
      documents[docOwner] = documents[docOwner].map(
        createDocumentMapper(documentKey, additionalProps)
      );
      documents.isCompanyDocUpdate = isDocUpdate;
    } else if (docOwner === KYC_ANNEXURE_DOCUMENTS) {
      documents[docOwner] = documents[docOwner].map(
        createDocumentMapper(documentKey, additionalProps)
      );
      documents.isKycDocUpdate = isDocUpdate;
    } else if (docOwner === OTHER_DOCUMENTS) {
      documents[docOwner] = documents[docOwner].map(
        createDocumentMapper(documentKey, additionalProps)
      );
    } else if (companyMultiDocs.includes(docOwner)) {
      documents[docOwner].documents = documents[docOwner].documents.map(
        createDocumentMapper(documentKey, additionalProps)
      );
      documents[docOwner].isDocUpdate = isDocUpdate;
    } else {
      if (stakeholderMultiDocs.includes(docOwner)) {
        const stakeholdersDocuments = documents[STAKEHOLDER_DOCUMENTS][stakeholderIndex][
          docOwner
        ].documents.map(createDocumentMapper(documentKey, additionalProps));

        documents[STAKEHOLDER_DOCUMENTS][stakeholderIndex][
          docOwner
        ].documents = stakeholdersDocuments;
        documents[STAKEHOLDER_DOCUMENTS][stakeholderIndex][docOwner].isDocUpdate = isDocUpdate;
      } else {
        const stakeholdersDocuments = documents[STAKEHOLDER_DOCUMENTS][
          stakeholderIndex
        ].documents.map(createDocumentMapper(documentKey, additionalProps));

        documents[STAKEHOLDER_DOCUMENTS][stakeholderIndex].documents = stakeholdersDocuments;
        documents[STAKEHOLDER_DOCUMENTS][stakeholderIndex].isDocUpdate = isDocUpdate;
      }
    }

    yield put(
      updateProspect({
        "prospect.documents": documents
      })
    );
  } catch (error) {
    yield put(uploadFilesFail({ [documentKey]: error }));
  } finally {
    if (yield cancelled()) {
      source.cancel();
    }
  }
}

export function* uploadDocumentsFlowSaga({ payload }) {
  yield race({
    task: call(uploadDocumentsBgSync, payload),
    cancel: take(
      action =>
        action.type === CANCEL_DOC_UPLOAD && action.payload.documentKey === payload.documentKey
    )
  });
}

export function* addOtherDocumentSaga({ payload }) {
  const config = cloneDeep(yield select(getAppConfig));

  config.prospect.documents.otherDocuments.push(payload);
  yield put(setConfig(config));
}

export function* deleteOtherDocumentSaga({ payload }) {
  const config = cloneDeep(yield select(getAppConfig));

  config.prospect.documents.otherDocuments = config.prospect.documents.otherDocuments.filter(
    doc => doc.documentKey !== payload
  );
  yield put(setConfig(config));
}

export function* downloadDocumentFileSaga({ payload: { prospectId, documentKey, fileName } }) {
  try {
    const headers = yield select(getAuthorizationHeader);
    const { data } = yield call(downloadProspectDocument.get, prospectId, documentKey, headers);
    const blob = new Blob([data], { type: data.type });
    yield call(saveAs, blob, fileName);
  } catch (error) {
    log(error);
  }
}

export function* addMultiDocumentSaga({ payload }) {
  const { stakeholderIndex, document } = payload;
  const config = cloneDeep(yield select(getAppConfig));
  if (document.documentType === COMPANY_BANK_STATEMENTS_DOCTYPE) {
    config.prospect.documents[COMPANY_BANK_STATEMENTS].documents.push(document);
  } else if (document.documentType === COMPANY_ADDRESS_PROOF_DOCTYPE) {
    config.prospect.documents[COMPANY_ADDRESS_PROOF].documents.push(document);
  } else if (document.documentType === COMPANY_INVOICES_DOCTYPE) {
    config.prospect.documents[COMPANY_INVOICES].documents.push(document);
  } else if (document.documentType === PERSONAL_BANK_STATEMENTS_DOCTYPE) {
    config.prospect.documents[STAKEHOLDER_DOCUMENTS][stakeholderIndex][
      PERSONAL_BANK_STATEMENTS
    ].documents.push(document);
  } else if (document.documentType === PERSONAL_BACKGROUND_DOCTYPE) {
    config.prospect.documents[STAKEHOLDER_DOCUMENTS][stakeholderIndex][
      PERSONAL_BACKGROUND
    ].documents.push(document);
  }

  yield put(setConfig(config));
}

export default function* uploadDocumentsSaga() {
  yield all([
    takeLatest(SAVE_AND_RETRIEVE_DOC_UPLOADER, saveProspectAndGetProspectDocumentsSaga),
    takeLatest(RETRIEVE_DOC_UPLOADER, getProspectDocumentsSaga),
    takeEvery(DOC_UPLOADER, uploadDocumentsFlowSaga),
    takeEvery(ADD_OTHER_DOCUMENT, addOtherDocumentSaga),
    takeEvery(DELETE_OTHER_DOCUMENT, deleteOtherDocumentSaga),
    takeLatest(DOWNLOAD_DOCUMENT_FILE, downloadDocumentFileSaga),
    takeEvery(ADD_MULTI_DOCUMENT, addMultiDocumentSaga)
  ]);
}
